import React, { useState, useEffect, useRef } from "react";
import PortalLayout from "@/components/PortalLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function PortalOrganisation() {
  const { user } = useAuth();
  const isLead = user?.role === "client_lead";

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAddr, setSavingAddr] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [memberForm, setMemberForm] = useState({ name: "", email: "", password: "" });
  const [addingSaving, setAddingSaving] = useState(false);
  const logoRef = useRef(null);

  const [addrForm, setAddrForm] = useState({
    line1: "", line2: "", city: "", postcode: "", country: "UK",
  });

  useEffect(() => {
    const requests = [api.get("/portal/organization")];
    if (isLead) requests.push(api.get("/portal/users"));

    Promise.all(requests).then(([orgRes, usersRes]) => {
      setOrg(orgRes.data);
      setAddrForm({
        line1: orgRes.data.billing_address?.line1 || "",
        line2: orgRes.data.billing_address?.line2 || "",
        city: orgRes.data.billing_address?.city || "",
        postcode: orgRes.data.billing_address?.postcode || "",
        country: orgRes.data.billing_address?.country || "UK",
      });
      if (usersRes) setMembers(usersRes.data);
    }).finally(() => setLoading(false));
  }, [isLead]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const r = await api.put("/portal/organization", { logo_image: ev.target.result });
        setOrg(r.data);
        toast.success("Logo updated");
      } catch { toast.error("Failed to update logo"); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removeLogo = async () => {
    try {
      const r = await api.put("/portal/organization", { logo_image: "" });
      setOrg(r.data);
      toast.success("Logo removed");
    } catch { toast.error("Failed to remove logo"); }
  };

  const saveBillingAddress = async () => {
    setSavingAddr(true);
    try {
      const r = await api.put("/portal/organization", { billing_address: addrForm });
      setOrg(r.data);
      toast.success("Billing address saved");
    } catch { toast.error("Failed to save billing address"); }
    finally { setSavingAddr(false); }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddingSaving(true);
    try {
      const r = await api.post("/portal/users", memberForm);
      setMembers((prev) => [...prev, r.data]);
      setShowDialog(false);
      setMemberForm({ name: "", email: "", password: "" });
      toast.success("Team member added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add member");
    } finally { setAddingSaving(false); }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Remove this team member?")) return;
    try {
      await api.delete(`/portal/users/${memberId}`);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast.success("Member removed");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to remove member"); }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="py-16 text-center text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div data-testid="portal-organisation-page">
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>
            {org?.name || "Organisation"}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>
            {isLead ? "Manage your organisation profile and team" : "Your organisation"}
          </p>
        </div>

        <div className="space-y-6">
          {/* Logo — lead only */}
          {isLead && (
            <div className="bg-white rounded-xl border p-6" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "#A8A29E" }}>
                Organisation Logo
              </p>
              <div className="flex items-center gap-6">
                <div className="relative flex-shrink-0">
                  <div
                    className="w-20 h-20 rounded-xl overflow-hidden flex items-center justify-center font-bold text-2xl text-white"
                    style={{ background: org?.logo_image ? "transparent" : "#D97706" }}
                  >
                    {org?.logo_image
                      ? <img src={org.logo_image} alt="logo" className="w-full h-full object-cover" />
                      : org?.name?.[0]?.toUpperCase()}
                  </div>
                  <button
                    data-testid="upload-logo-button"
                    onClick={() => logoRef.current?.click()}
                    className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full border-2 border-white flex items-center justify-center"
                    style={{ background: "#292524" }}
                  >
                    <Camera size={12} style={{ color: "#fff" }} />
                  </button>
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                </div>
                <div>
                  <p className="text-sm" style={{ color: "#57534E" }}>Upload your organisation's logo</p>
                  <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>Max 2 MB · PNG, JPG, WebP</p>
                  {org?.logo_image && (
                    <button
                      data-testid="remove-logo-button"
                      onClick={removeLogo}
                      className="mt-2 text-xs px-2 py-1 rounded border"
                      style={{ borderColor: "#E7E5E4", color: "#78716C" }}
                    >
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Billing Address — lead only */}
          {isLead && (
            <div className="bg-white rounded-xl border p-6 space-y-4" style={{ borderColor: "#E7E5E4" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>
                Billing Address
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-xs" style={{ color: "#57534E" }}>Address Line 1</Label>
                  <Input
                    data-testid="billing-address-line1"
                    value={addrForm.line1}
                    onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })}
                    className="mt-1"
                    placeholder="123 Business Street"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs" style={{ color: "#57534E" }}>Address Line 2</Label>
                  <Input
                    data-testid="billing-address-line2"
                    value={addrForm.line2}
                    onChange={(e) => setAddrForm({ ...addrForm, line2: e.target.value })}
                    className="mt-1"
                    placeholder="Suite 100 (optional)"
                  />
                </div>
                <div>
                  <Label className="text-xs" style={{ color: "#57534E" }}>City</Label>
                  <Input
                    data-testid="billing-address-city"
                    value={addrForm.city}
                    onChange={(e) => setAddrForm({ ...addrForm, city: e.target.value })}
                    className="mt-1"
                    placeholder="London"
                  />
                </div>
                <div>
                  <Label className="text-xs" style={{ color: "#57534E" }}>Postcode</Label>
                  <Input
                    data-testid="billing-address-postcode"
                    value={addrForm.postcode}
                    onChange={(e) => setAddrForm({ ...addrForm, postcode: e.target.value })}
                    className="mt-1"
                    placeholder="EC1A 1BB"
                  />
                </div>
                <div>
                  <Label className="text-xs" style={{ color: "#57534E" }}>Country</Label>
                  <Input
                    data-testid="billing-address-country"
                    value={addrForm.country}
                    onChange={(e) => setAddrForm({ ...addrForm, country: e.target.value })}
                    className="mt-1"
                    placeholder="UK"
                  />
                </div>
              </div>
              <Button
                data-testid="save-billing-address-button"
                onClick={saveBillingAddress}
                disabled={savingAddr}
                style={{ background: "#292524", color: "#fff" }}
              >
                {savingAddr ? "Saving..." : "Save Address"}
              </Button>
            </div>
          )}

          {/* Team Members */}
          <div className="bg-white rounded-xl border" style={{ borderColor: "#E7E5E4" }}>
            <div
              className="px-6 py-4 border-b flex items-center justify-between"
              style={{ borderColor: "#E7E5E4" }}
            >
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>
                Team Members
              </p>
              {isLead && (
                <Button
                  size="sm"
                  data-testid="add-member-button"
                  onClick={() => setShowDialog(true)}
                  style={{ background: "#D97706", color: "#fff" }}
                >
                  <Plus size={13} className="mr-1" />
                  Add Member
                </Button>
              )}
            </div>
            <div className="divide-y" style={{ borderColor: "#F5F3EF" }}>
              {members.length === 0 ? (
                <p className="px-6 py-8 text-sm text-center" style={{ color: "#A8A29E" }}>No members yet</p>
              ) : (
                members.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`member-${m.id}`}
                    className="px-6 py-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold overflow-hidden"
                        style={{ background: m.avatar_image ? "transparent" : (m.avatar_color || "#D97706") }}
                      >
                        {m.avatar_image
                          ? <img src={m.avatar_image} alt="" className="w-full h-full object-cover" />
                          : m.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: "#292524" }}>{m.name}</p>
                        <p className="text-xs" style={{ color: "#A8A29E" }}>{m.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className="text-xs px-2.5 py-1 rounded-full font-medium"
                        style={{
                          background: m.role === "client_lead" ? "#FEF3C7" : "#F5F3EF",
                          color: m.role === "client_lead" ? "#D97706" : "#57534E",
                        }}
                      >
                        {m.role === "client_lead" ? "Lead" : "Member"}
                      </span>
                      {isLead && m.id !== user?.id && m.role !== "client_lead" && (
                        <button
                          data-testid={`remove-member-${m.id}`}
                          onClick={() => handleRemoveMember(m.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                          style={{ color: "#A8A29E" }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Add Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input
                data-testid="member-name-input"
                required
                value={memberForm.name}
                onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                data-testid="member-email-input"
                type="email"
                required
                value={memberForm.email}
                onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                data-testid="member-password-input"
                type="password"
                required
                value={memberForm.password}
                onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addingSaving}
                style={{ background: "#D97706", color: "#fff" }}
              >
                {addingSaving ? "Adding..." : "Add Member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
